import { useMemo, useEffect } from "react"
import { useLocation } from "react-router-dom"
import { Users, Briefcase, Palette, ShoppingCart, Settings, HelpCircle } from "lucide-react"
import { AppShell, type NavItem } from "@shadcn/components/layouts/AppShell"
import type { Company, CombinedUser } from "shared"
import { useConfigContext } from "shared"
import { useFeaturesContext } from "shared/src/libs/featuresLib/featuresLib"
import { Skeleton } from "@shadcn/components/ui/skeleton"
import { getBrandingMenuItems } from "../Template/TemplateTable/getBrandingMenuItems"
import { useBreadcrumbs } from "shared/src/contexts"

interface AppShellPublicProps {
	accessIdToken?: string
	clientId?: string
	evoEnabled?: boolean
	children: React.ReactNode
	company: Company | null
	user?: CombinedUser | null
	userName?: string
	onLogout?: () => void
	logo?: string | null
	showNavbar?: boolean
	isAuthenticating?: boolean
}

export function AppShellPublic({
	accessIdToken,
	clientId,
	evoEnabled,
	children,
	company,
	user,
	userName,
	onLogout,
	logo,
	showNavbar = true,
	isAuthenticating = false,
}: Readonly<AppShellPublicProps>) {
	const location = useLocation()
	const { accessPermissions } = useConfigContext()
	const { permissions } = useFeaturesContext()
	const { breadcrumbs, setDefaultBreadcrumbs } = useBreadcrumbs()

	const navItems = useMemo<NavItem[]>(() => {
		const items: NavItem[] = [{ label: "Candidates", href: "/candidates", icon: <Users size={20} /> }]

		if (permissions.fullJobDescription) {
			items.push({ label: "Jobs", href: "/jobs", icon: <Briefcase size={20} /> })
		}

		if (permissions.salesAssets) {
			items.push({ label: "Sales Assets", href: "/sales-assets", icon: <ShoppingCart size={20} /> })
		}

		// Get branding permission for the user's role
		const userRole = user?.role || "recruiter"
		const brandingPermission = accessPermissions?.[userRole]?.branding || "none"

		// Get branding menu items based on permissions
		const brandingMenuItems = getBrandingMenuItems(
			{
				placeholderTags: permissions.placeholderTags,
				uniqueLinks: permissions.uniqueLinks,
				brandingAccess: brandingPermission,
			},
			user,
			company
		)

		// Only add Branding section if user has access to any branding items
		if (brandingMenuItems.length > 0) {
			items.push({
				label: "Branding",
				href: "/branding",
				icon: <Palette size={20} />,
				children: brandingMenuItems.map((item) => ({
					label: item.label,
					href: item.href,
					icon: undefined,
				})),
			})
		}

		return items
	}, [company, user, accessPermissions, permissions])

	// Help Center and Settings at bottom of sidebar
	const footerNavItems = useMemo<NavItem[]>(
		() => [
			{ label: "Help Center", href: "https://help.hireara.ai/en/", icon: <HelpCircle size={20} /> },
			{ label: "Settings", href: "/settings", icon: <Settings size={20} /> },
		],
		[]
	)

	// Generate default route-based breadcrumbs
	const defaultBreadcrumbs = useMemo(() => {
		const path = location.pathname
		if (path.startsWith("/application/")) return [{ label: "Candidates", href: "/candidates" }]
		if (path.startsWith("/candidates")) return [{ label: "Candidates", href: "/candidates" }]
		if (path.startsWith("/jobs")) return [{ label: "Jobs", href: "/jobs" }]
		if (path.startsWith("/branding")) {
			const breadcrumbs = [{ label: "Branding", href: "/branding" }]
			// Parse the branding section parameter from the path (e.g., "Templates", "AI Builder")
			const pathParts: string[] = path.split("/").filter((part: string) => Boolean(part))
			if (pathParts.length > 1 && pathParts[1]) {
				const brandingSection: string = pathParts[1]
				const sectionLabel: string = decodeURIComponent(brandingSection)
				breadcrumbs.push({ label: sectionLabel, href: `/branding/${brandingSection}` })
			}
			return breadcrumbs
		}
		if (path.startsWith("/sales-assets")) return [{ label: "Sales Assets", href: "/sales-assets" }]
		if (path.startsWith("/settings")) return [{ label: "Settings", href: "/settings" }]
		return []
	}, [location.pathname])

	// Update breadcrumbs when route changes (skipped if a child page set custom breadcrumbs)
	useEffect(() => {
		setDefaultBreadcrumbs(defaultBreadcrumbs, location.pathname)
	}, [defaultBreadcrumbs, setDefaultBreadcrumbs, location.pathname])

	if (isAuthenticating) {
		return (
			<div className="flex h-screen w-full items-center justify-center">
				<div className="flex flex-col items-center gap-4">
					<Skeleton className="h-12 w-12 rounded-full" />
					<Skeleton className="h-4 w-32" />
				</div>
			</div>
		)
	}

	if (!showNavbar) {
		return <div className="min-h-screen">{children}</div>
	}

	return (
		<AppShell
			navItems={navItems}
			footerNavItems={footerNavItems}
			logo={null}
			defaultOpen={true}
			header={{
				accessIdToken,
				clientId,
				evoEnabled,
				breadcrumbs,
				company: company
					? {
							name: company.name || "Company",
							initial: company.name?.charAt(0).toUpperCase() || "C",
							color: "bg-red-600",
						}
					: undefined,
				user:
					user || userName
						? {
								name: user?.name || userName || "",
								email: user?.email || userName || "",
							}
						: undefined,
				onLogout,
				logo,
			}}>
			{children}
		</AppShell>
	)
}

export default AppShellPublic
